import React, { useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Languages, Lock } from "lucide-react";
import { useLang } from "../context/LangContext";
import { usePagesVisibility } from "../context/PagesContext";
import ConvergenLogo from "./ConvergenMark";
import { Button } from "./ui/button";

export default function Navbar() {
  const { t, lang, toggleLang } = useLang();
  const { pages } = usePagesVisibility();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const allLinks = [
    { to: "/", label: t.nav.home, testid: "nav-home", key: "home" },
    { to: "/quienes-somos", label: t.nav.about, testid: "nav-about", key: "about" },
    { to: "/servicios", label: t.nav.services, testid: "nav-services", key: "services" },
    { to: "/iieas", label: t.nav.iieas, testid: "nav-iieas", key: "iieas" },
    { to: "/synapse", label: t.nav.synapse, testid: "nav-synapse", key: "synapse" },
    { to: "/insights", label: t.nav.insights, testid: "nav-insights", key: "insights" },
    { to: "/contacto", label: t.nav.contact, testid: "nav-contact", key: "contact" },
  ];
  const links = allLinks.filter((l) => pages[l.key] !== false);

  const isContact = location.pathname === "/contacto";

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-rule">
      <div className="page-container flex items-center justify-between h-20">
        <Link to="/" data-testid="brand-link" className="shrink-0">
          <ConvergenLogo size={52} />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={l.testid}
              className={({ isActive }) =>
                `nav-link text-[13px] tracking-wide ${isActive ? "active" : ""}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggleLang}
            data-testid="lang-toggle"
            className="hidden sm:flex items-center gap-1.5 text-[12px] font-mono tracking-widest text-ink hover:text-cerulean transition-colors"
            aria-label="Toggle language"
          >
            <Languages className="w-4 h-4" strokeWidth={1.5} />
            {lang.toUpperCase()}
          </button>
          <Link
            to="/admin/login"
            data-testid="nav-cms"
            title="Editor de Contenido (CMS)"
            className="hidden sm:flex items-center gap-1.5 text-[12px] font-mono tracking-widest text-ink hover:text-cerulean transition-colors border-l border-rule pl-3"
          >
            <Lock className="w-3.5 h-3.5" strokeWidth={1.5} />
            CMS
          </Link>
          {!isContact && (
            <Link to="/contacto" data-testid="nav-cta">
              <Button
                className="btn-sweep btn-sweep-light hidden md:inline-flex bg-ink text-white rounded-none px-5 h-10 text-[12px] tracking-[0.18em] uppercase font-medium"
              >
                {t.nav.cta}
              </Button>
            </Link>
          )}
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen((p) => !p)}
            className="lg:hidden p-2 text-ink"
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-rule bg-white" data-testid="mobile-menu">
          <nav className="page-container py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-${l.testid}`}
                className={({ isActive }) =>
                  `nav-link text-[14px] ${isActive ? "active" : ""}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <button
              onClick={toggleLang}
              data-testid="mobile-lang-toggle"
              className="flex items-center gap-2 text-[13px] font-mono tracking-widest text-ink mt-2"
            >
              <Languages className="w-4 h-4" strokeWidth={1.5} />
              {lang.toUpperCase()}
            </button>
            <Link
              to="/admin/login"
              data-testid="mobile-nav-cms"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 text-[13px] font-mono tracking-widest text-ink hover:text-cerulean transition-colors"
            >
              <Lock className="w-3.5 h-3.5" strokeWidth={1.5} /> CMS · Admin
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
